import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { BUSINESS, STATS } from "@/data";
import { MaskedLines, Counter, Magnetic, Eyebrow } from "@/components/site/motion-lib";

const HERO_IMG =
  "https://images.pexels.com/photos/177872/pexels-photo-177872.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1500";

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const overlayY = useTransform(scrollYProgress, [0, 1], ["0%", "-8%"]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden">
      {/* Parallax background */}
      <motion.div style={reduce ? {} : { y: imgY }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="Forgia con scintille durante la lavorazione del ferro" className="h-[112%] w-full object-cover object-center" loading="eager" />
        <div className="absolute inset-0 bg-[#141414]/68" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141414] via-[#141414]/35 to-[#141414]/55" />
      </motion.div>
      <div className="absolute inset-0 -z-10 blueprint-grid opacity-60" aria-hidden="true" />

      {/* Spark burst on load */}
      {!reduce && (
        <div className="pointer-events-none absolute left-[16%] top-[34%] -z-[5]" aria-hidden="true">
          <span
            className="block h-40 w-40 rounded-full"
            style={{
              background: "radial-gradient(circle, rgba(200,155,74,0.55) 0%, rgba(181,80,47,0.25) 40%, transparent 70%)",
              animation: "sparkGlow 1.6s ease-out 0.3s both",
            }}
          />
        </div>
      )}

      <motion.div style={reduce ? {} : { y: overlayY }} className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-14 pt-32">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <Eyebrow>Fabbro artigiano-serramentista · Montespertoli (FI)</Eyebrow>
        </motion.div>

        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-[#F5F1EA] mt-6 text-[13vw] sm:text-7xl md:text-8xl lg:text-[7.5rem]">
          <MaskedLines
            lines={[
              <>Lavoriamo il ferro</>,
              <>e l'alluminio da <span className="italic text-[#C89B4A]">tre generazioni</span></>,
            ]}
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-7 max-w-xl text-lg sm:text-xl text-[#F5F1EA]/80 leading-relaxed"
        >
          Tutti i serramenti e tutta la carpenteria metallica di cui hai bisogno, <span className="text-[#C89B4A] font-semibold">interamente artigianali!</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="mt-9 flex flex-col sm:flex-row gap-4"
        >
          <Magnetic strength={0.25}>
            <a
              href={BUSINESS.phoneHref}
              data-testid="hero-call-cta"
              className="group inline-flex w-full sm:w-auto items-center justify-center gap-3 rounded-full bg-[#B5502F] px-8 py-4 text-base font-semibold text-[#F5F1EA] hover:bg-[#c95c39] transition-colors duration-300"
            >
              <Phone size={20} strokeWidth={1.75} className="group-hover:rotate-12 transition-transform" />
              Chiama ora · {BUSINESS.phone}
            </a>
          </Magnetic>
          <Magnetic strength={0.25}>
            <a
              href="#contatti"
              data-testid="hero-quote-cta"
              className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full border border-[#C89B4A]/50 px-8 py-4 text-base font-semibold text-[#C89B4A] hover:bg-[#C89B4A]/10 transition-colors duration-300"
            >
              Richiedi preventivo
            </a>
          </Magnetic>
        </motion.div>

        {/* Trust badges / counters */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-14 grid grid-cols-3 gap-px bg-[#F5F1EA]/10 border border-[#F5F1EA]/10 rounded-xl overflow-hidden"
        >
          {STATS.map((s) => (
            <div key={s.label} className="bg-[#1c1c1c]/60 backdrop-blur-sm px-5 py-6">
              <div className="font-display text-2xl md:text-3xl font-bold text-[#C89B4A] leading-tight">
                {s.text ? s.text : <Counter value={s.value} suffix={s.suffix} decimals={s.decimals || 0} />}
              </div>
              <div className="mt-1 text-xs text-[#F5F1EA]/60">{s.labelOverride || s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>

      <motion.a
        href="#servizi"
        aria-label="Scorri ai servizi"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex"
      >
        <motion.span animate={reduce ? {} : { y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} className="text-[#F5F1EA]/50">
          <ArrowDown size={22} strokeWidth={1.5} />
        </motion.span>
      </motion.a>
    </section>
  );
}
